"use client";

import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Box,
  HStack,
  IconButton,
  Divider,
  Checkbox,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { Link, FileUp, FileText } from "lucide-react";
import {
  knowledgeBaseService,
  type KnowledgeBaseText,
} from "../../voice-services/knowledgeBaseService";
import { AddTextModal } from "./AddTextModal";
import { AddWebPagesModal } from "./AddWebPagesModal";
import { useAuth } from "../../contexts/AuthContext";

interface AddKnowledgeBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddKnowledgeBaseModal: React.FC<AddKnowledgeBaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { isAdmin, currentClientId, clients } = useAuth();

  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [texts, setTexts] = useState<KnowledgeBaseText[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [urls, setUrls] = useState<string[]>([]);
  const [enableAutoRefresh, setEnableAutoRefresh] = useState(false);

  const [isAddTextModalOpen, setIsAddTextModalOpen] = useState(false);
  const [isAddWebPagesModalOpen, setIsAddWebPagesModalOpen] = useState(false);

  const toast = useToast();

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleAddText = (text: KnowledgeBaseText) => {
    setTexts([...texts, text]);
    setIsAddTextModalOpen(false);
  };

  const handleAddWebPages = (newUrls: string[]) => {
    setUrls([...urls, ...newUrls]);
    setIsAddWebPagesModalOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles([...files, ...newFiles]);
    }
  };

  const handleRemoveText = (index: number) => {
    setTexts(texts.filter((_, i) => i !== index));
  };

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleRemoveUrl = (index: number) => {
    setUrls(urls.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({
        title: "Name is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (texts.length === 0 && files.length === 0 && urls.length === 0) {
      toast({
        title: "At least one source is required",
        description: "Add text, files, or web pages",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const currentClient = clients.find((c) => c.id === currentClientId);
      const companyName =
        isAdmin && currentClient ? currentClient.company_name : undefined;

      await knowledgeBaseService.create(
        name,
        texts,
        files,
        urls,
        enableAutoRefresh,
        companyName
      );
      toast({
        title: "Knowledge base created successfully",
        status: "success",
        duration: 3000,
      });
      onSuccess();
      onClose();
      setName("");
      setTexts([]);
      setFiles([]);
      setUrls([]);
      setEnableAutoRefresh(false);
    } catch (error: any) {
      toast({
        title: "Error creating knowledge base",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent bg={"white"}>
          <ModalHeader color={"gray.800"}>Add Voice Knowledge Base</ModalHeader>
          <ModalBody>
            <VStack spacing={6} align="stretch">
              <FormControl isRequired>
                <FormLabel color={"gray.700"}>Knowledge Base Name</FormLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter name"
                  bg={"white"}
                  borderColor={"gray.300"}
                  color={"gray.800"}
                  maxLength={40}
                />
              </FormControl>

              <Box>
                <VStack spacing={2} align="stretch" mb={4}>
                  {texts.length > 0 && (
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        mb={1}
                        color={"gray.600"}
                      >
                        Texts
                      </Text>
                      {texts.map((text, index) => (
                        <HStack
                          key={index}
                          p={2}
                          bg={"gray.100"}
                          borderRadius="md"
                          justify="space-between"
                          mt={1}
                        >
                          <Text fontSize="sm" noOfLines={1} color={"gray.700"}>
                            {text.title}
                          </Text>
                          <IconButton
                            aria-label="Remove text"
                            icon={<CloseIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleRemoveText(index)}
                          />
                        </HStack>
                      ))}
                    </Box>
                  )}

                  {files.length > 0 && (
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        mb={1}
                        color={"gray.600"}
                      >
                        Files
                      </Text>
                      {files.map((file, index) => (
                        <HStack
                          key={index}
                          p={2}
                          bg={"gray.100"}
                          borderRadius="md"
                          justify="space-between"
                          mt={1}
                        >
                          <Text fontSize="sm" noOfLines={1} color={"gray.700"}>
                            {file.name}
                          </Text>
                          <IconButton
                            aria-label="Remove file"
                            icon={<CloseIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleRemoveFile(index)}
                          />
                        </HStack>
                      ))}
                    </Box>
                  )}

                  {urls.length > 0 && (
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        mb={1}
                        color={"gray.600"}
                      >
                        Web Pages
                      </Text>
                      {urls.map((url, index) => (
                        <HStack
                          key={index}
                          p={2}
                          bg={"gray.100"}
                          borderRadius="md"
                          justify="space-between"
                          mt={1}
                        >
                          <Text fontSize="sm" noOfLines={1} color={"gray.700"}>
                            {url}
                          </Text>
                          <IconButton
                            aria-label="Remove URL"
                            icon={<CloseIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleRemoveUrl(index)}
                          />
                        </HStack>
                      ))}
                      <FormControl>
                        <Checkbox
                          isChecked={enableAutoRefresh}
                          onChange={(e) =>
                            setEnableAutoRefresh(e.target.checked)
                          }
                          colorScheme="gray"
                          size="sm"
                          mt={2}
                        >
                          <Text fontSize="sm" color={"gray.700"}>
                            Enable auto refresh (updates every 12 hours)
                          </Text>
                        </Checkbox>
                      </FormControl>
                    </Box>
                  )}
                </VStack>

                <Divider my={4} borderColor={"gray.200"} />

                <HStack spacing={4} mt={4}>
                  <Button
                    leftIcon={<Link size={16} />}
                    variant="outline"
                    onClick={() => setIsAddWebPagesModalOpen(true)}
                    color={"gray.700"}
                    borderColor={"gray.300"}
                    _hover={{ bg: "gray.100" }}
                  >
                    Add Web Pages
                  </Button>
                  <Button
                    leftIcon={<FileUp size={16} />}
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    color={"gray.700"}
                    borderColor={"gray.300"}
                    _hover={{ bg: "gray.100" }}
                  >
                    Upload Files
                  </Button>
                  <Button
                    leftIcon={<FileText size={16} />}
                    variant="outline"
                    onClick={() => setIsAddTextModalOpen(true)}
                    color={"gray.700"}
                    borderColor={"gray.300"}
                    _hover={{ bg: "gray.100" }}
                  >
                    Add Text
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx"
                  />
                </HStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              colorScheme={"white"}
              textColor={"gray.800"}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              bg="primary.800"
              _hover={{ bg: "primary.900" }}
              onClick={handleSubmit}
              isLoading={isLoading}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AddTextModal
        isOpen={isAddTextModalOpen}
        onClose={() => setIsAddTextModalOpen(false)}
        onSave={handleAddText}
      />

      <AddWebPagesModal
        isOpen={isAddWebPagesModalOpen}
        onClose={() => setIsAddWebPagesModalOpen(false)}
        onSave={handleAddWebPages}
      />
    </>
  );
};
